<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'

import AppScreen from '@/components/layout/AppScreen.vue'
import ScreenTabs from '@/components/layout/ScreenTabs.vue'
import { useTransitIndex } from '@/composables/useTransitIndex'
import type { IndexedStop, RouteAtStop } from '@/composables/useTransitIndex'

import { STOP_TABS } from './tabs'

const props = defineProps<{ stopId: string }>()

const router = useRouter()
const { index, isPending } = useTransitIndex()

/** Params are strings and user-editable, so parse rather than trust. */
const numericId = computed(() => {
  const parsed = Number(props.stopId)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined
})

const stop = computed<IndexedStop | undefined>(() =>
  numericId.value === undefined ? undefined : index.value?.stopsById.get(numericId.value),
)

const routesAtStop = computed<RouteAtStop[]>(() =>
  numericId.value === undefined ? [] : (index.value?.routesByStopId.get(numericId.value) ?? []),
)

/**
 * A stop id that is not in the dataset is a dead end — a stale link or a hand
 * edited URL. Send the user somewhere usable instead of rendering an empty
 * screen. Waits for the dataset, or it would fire on every cold load.
 */
watch(
  [index, stop],
  ([loadedIndex, foundStop]) => {
    if (loadedIndex && !foundStop) router.replace({ name: 'track' })
  },
  { immediate: true },
)

defineExpose({ stop, routesAtStop })
</script>

<template>
  <AppScreen title="Tracking" leading="back">
    <template #tabs>
      <ScreenTabs :tabs="STOP_TABS" />
    </template>

    <div v-if="stop" class="border-b bg-muted px-3 py-2">
      <p class="truncate text-sm font-bold">{{ stop.name }}</p>
    </div>

    <p v-if="isPending" class="p-4 text-xs text-muted-foreground">Loading network data…</p>
    <slot v-else :stop="stop" :routes-at-stop="routesAtStop" />
  </AppScreen>
</template>
